"use strict";
import { jsxs, jsx } from 'react/jsx-runtime';
import React from 'react';
import { useColorModeValue, Stack, Badge, HStack, Text, Box, InputGroup, Input, InputRightElement, IconButton, Icon, List } from '@chakra-ui/react';
import { useMultipleSelection, useCombobox } from 'downshift';
import { BiChevronUp, BiChevronDown } from 'react-icons/bi';

const MultipleComboBox = ({
  selectedItems,
  onChange,
  options,
  filterMenuOptions,
  itemToString,
  placeholder,
  size,
  showSelectedItems = true,
  menuProps,
  colorScheme = "blue",
  ...rest
}) => {
  const [inputValue, setInputValue] = React.useState("");
  const menuOptions = React.useMemo(
    () => filterMenuOptions(options, selectedItems, inputValue),
    [selectedItems, inputValue, options, filterMenuOptions]
  );
  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } = useMultipleSelection({
    selectedItems,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onChange(newSelectedItems);
          break;
      }
    }
  });
  const { isOpen, getToggleButtonProps, getMenuProps, getInputProps, getItemProps, highlightedIndex } = useCombobox({
    items: menuOptions,
    itemToString,
    selectedItem: null,
    inputValue,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: 0
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            onChange([...selectedItems, newSelectedItem]);
            setInputValue("");
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue);
          break;
      }
    }
  });
  const menuBg = useColorModeValue("white", "gray.800");
  const hoverColor = useColorModeValue("gray.100", "gray.700");
  const borderColor = useColorModeValue("blackAlpha.300", "whiteAlpha.300");
  return /* @__PURE__ */ jsxs(Stack, { children: [
    showSelectedItems && selectedItems.map((selectedItemForRender, index) => {
      const displayName = itemToString(selectedItemForRender);
      return /* @__PURE__ */ jsx(
        Badge,
        {
          size,
          alignSelf: "flex-start",
          ...getSelectedItemProps({
            selectedItem: selectedItemForRender,
            index
          }),
          whiteSpace: "break-spaces",
          fontWeight: "medium",
          borderRadius: "md",
          px: 2,
          py: 0.5,
          colorScheme,
          textTransform: "initial",
          children: /* @__PURE__ */ jsxs(HStack, { spacing: 1, children: [
            /* @__PURE__ */ jsx(Text, { children: displayName }),
            /* @__PURE__ */ jsx(
              Box,
              {
                as: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  removeSelectedItem(selectedItemForRender);
                },
                children: "\u2715"
              }
            )
          ] })
        },
        displayName
      );
    }),
    /* @__PURE__ */ jsxs(Stack, { position: "relative", children: [
      /* @__PURE__ */ jsxs(InputGroup, { size, children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            borderRadius: "md",
            placeholder,
            colorScheme,
            ...rest,
            ...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))
          }
        ),
        /* @__PURE__ */ jsx(InputRightElement, { children: /* @__PURE__ */ jsx(
          IconButton,
          {
            size,
            "aria-label": "toggle menu",
            type: "button",
            variant: "ghost",
            icon: /* @__PURE__ */ jsx(Icon, { as: isOpen ? BiChevronUp : BiChevronDown }),
            ...getToggleButtonProps()
          }
        ) })
      ] }),
      /* @__PURE__ */ jsx(
        List,
        {
          w: "100%",
          top: size === "lg" ? "48px" : "40px",
          position: "absolute",
          zIndex: "popover",
          boxShadow: "md",
          borderRadius: "md",
          overflow: "hidden",
          overflowY: "auto",
          background: menuBg,
          border: `1px solid`,
          borderColor,
          display: isOpen ? void 0 : "none",
          ...menuProps,
          ...getMenuProps(),
          children: isOpen && menuOptions.map((item, index) => /* @__PURE__ */ jsx(
            Stack,
            {
              cursor: "pointer",
              "aria-label": "menu item",
              px: 2,
              py: 2,
              as: "li",
              bg: highlightedIndex === index ? hoverColor : void 0,
              _hover: { bg: hoverColor },
              ...getItemProps({ item, index }),
              children: /* @__PURE__ */ jsx(Text, { fontWeight: "medium", fontSize: "sm", children: itemToString(item) }, itemToString(item))
            },
            `${itemToString(item)}${index}`
          ))
        }
      )
    ] })
  ] });
};

export { MultipleComboBox as default };
